import type { UICalendarEvent, UnionIconValues } from '@repo/common';
import type {
    Filter,
    FilteredItemType,
    FilterOption,
    Section,
    SectionState,
    SectionWithMetadata
} from '@repo/common/types/FilterSet';
import { createMultiSelectFilter } from '@repo/common/types/FilterSet';
import { providerCalendars } from '@repo/sharedlib/src/stores/provider.calendars.store';
import { workspaceStore } from '@repo/sharedlib/src/stores/workspace.store';
import { derived, get, writable, type Readable, type Writable } from 'svelte/store';
import { createCalendarFilter, createSpaceFilter, createTagFilter } from './filters';

import type { IFilteredType } from '@repo/common/types/FilterSet';

/**
 * Abstract base class for creating polymorphic filtering systems.
 *
 * This class provides a complete filtering and sectioning system for different item types.
 * It handles reactive filtering, section generation, and filter option management with counts.
 *
 * @template T - The input item type (must extend FilteredItemType)
 * @template U - The output item type for UI consumption (defaults to T)

 *
 * ## Architecture Overview
 *
 * The FilteredType system works in the following sequence:
 *
 * 1. **Input**: Raw items of type T from a Svelte store
 * 2. **Filtering**: Items are filtered based on current filter values
 * 3. **Mapping**: Filtered items are transformed from T to U using mapItem()
 * 4. **Sectioning**: Mapped items are grouped into sections using generateSections()
 * 5. **Canonical Filters**: Base filters are generated as a derived store from workspace and static values
 * 6. **Filter Counts**: Counts are calculated based on all items (not filtered items) to avoid loops
 *
 * ## Key Features
 *
 * - **Reactive**: All derived stores automatically update when inputs change
 * - **Type Safe**: Full TypeScript support with generic input/output types
 * - **Performance**: Efficient filtering and counting with minimal re-computation
 * - **Extensible**: Easy to create new filtered types by extending this class
 * - **UI Ready**: Exposes stores and values ready for Svelte component consumption
 * - **Individual Filter Stores**: Each filter type has its own store for efficient updates
 * - **Loop Prevention**: Counts are based on all items, not filtered items, to prevent circular dependencies
 *
 * ## Implementation Checklist
 *
 * When creating a new FilteredType subclass, implement these required methods:
 *
 * - ✅ `mapItem(item: T): U` - Transform input items to output items
 * - ✅ `generateSections(items: U[]): Section<U>[]` - Group items into sections
 *
 * ## Available Stores
 *
 * After implementation, your subclass will have these reactive stores:
 *
 * - `sections` - Readable store of grouped and mapped items
 * - `itemsStore` - Readable store of flat list of mapped items
 * - `filterTagValues` - Writable store for tag filter values
 * - `filterSpaceValues` - Writable store for space filter values
 * - `filterKeywordValue` - Writable store for keyword filter value
 * - `filterCalendarValues` - Writable store for calendar filter values
 * - `filterDatePresetValues` - Writable store for date preset filter values
 * - `filterShowCompletedValue` - Writable store for show completed filter value
 * - `filterMemoryValues` - Writable store for memory filter values
 * - `filters` - Readable store of canonical filter options (without counts)
 * - `filtersWithCounts` - Readable store of filter options with counts
 *
 * ## Common Patterns
 *
 * - Use helper methods from filters.ts for common filter types
 * - Implement switch statements in calculateCountForFilterOption for different filter types
 * - Use time-based or category-based sectioning strategies
 * - Handle missing data gracefully in mapItem with default values
 * - Consider performance for large datasets in count calculations
 */
export abstract class FilteredType<T extends FilteredItemType, U = T>
  implements IFilteredType<T, U>
{
  protected items: Readable<T[]>;

  // Individual filter stores for efficient updates
  public readonly filterTagValues: Writable<string[]> = writable([]);
  public readonly filterSpaceValues: Writable<string[]> = writable([]);
  public readonly filterKeywordValue: Writable<string> = writable('');
  public readonly filterCalendarValues: Writable<string[]> = writable([]);
  public readonly filterDatePresetValues: Writable<string[]> = writable([]);
  public readonly filterShowCompletedValue: Writable<boolean> = writable(true);
  public readonly filterMemoryValues: Writable<string[]> = writable([]);
  public readonly filterFavoriteValue: Writable<boolean> = writable(false);

  // Limit store for pagination/performance
  public readonly limit: Writable<number> = writable(50);

  // Flag to enable/disable expensive filter counts
  private enableFilterCounts: boolean = false;

  // Selective count enablement for specific filter types
  private enabledCountTypes: Set<string> = new Set(['Spaces']); // Enable spaces counts by default

  // Derived intermediate type for filtered items
  public readonly filteredItems: Readable<T[]>;

  // Public readonly store for unlimited mapped items (for virtual scrolling)
  public readonly mappedItems: Readable<U[]>;

  // Public readable derived store for filtered sections (with mapped output type)
  public readonly sections: Readable<Section<U>[]>;

  // Public readable derived store for unlimited sections
  public readonly unlimitedSections: Readable<Section<U>[]>;

  // Public readable store for sections with applied state (collapsed, displayLimit)
  public readonly sectionsWithState: Readable<SectionWithMetadata<U>[]>;

  // Canonical filters derived store (without counts) - based on workspace and static values
  public readonly filters: Readable<Omit<Filter, 'counts'>[]>;

  // Public readable derived store for filters with counts - based on all items and canonical filters
  public readonly filtersWithCounts: Readable<Filter[]>;

  // Optional set of item IDs that match an external search for the current keyword.
  // Subclasses (e.g., FilteredMemory) can populate this to override simple substring matching.
  protected readonly keywordIdSet: Writable<Set<string> | null> = writable(null);
  // Optional ordered list of IDs representing ranking from an external search
  protected readonly keywordIdOrder: Writable<string[] | null> = writable(null);

  // Section state management for collapsible sections
  protected sectionStates: Writable<Map<string, SectionState>> = writable(new Map());

  protected sortItems(items: T[]): T[] {
    return [...items].sort((a: T, b: T) => {
      const dateA = new Date(this.getItemCreatedAt(a));
      const dateB = new Date(this.getItemCreatedAt(b));
      return dateB.getTime() - dateA.getTime();
    });
  }

  protected getItemCreatedAt(item: T): Date {
    return new Date((item as any).createdAt || (item as UICalendarEvent).start || new Date());
  }

  // Get section ID (defaults to title, can use config.id if available)
  protected getSectionId(section: Section<U> | SectionWithMetadata<U>): string {
    return (section as SectionWithMetadata<U>).config?.id || section.title;
  }

  // Toggle section collapsed state
  public toggleSectionCollapsed(sectionId: string): void {
    this.sectionStates.update((states) => {
      const newStates = new Map(states);
      const current = newStates.get(sectionId) || { collapsed: false, displayLimit: 10 };
      newStates.set(sectionId, { ...current, collapsed: !current.collapsed });
      return newStates;
    });
  }

  // Update section display limit for show more
  public updateSectionDisplayLimit(sectionId: string, newLimit: number): void {
    this.sectionStates.update((states) => {
      const newStates = new Map(states);
      const current = newStates.get(sectionId) || { collapsed: false, displayLimit: 10 };
      newStates.set(sectionId, { ...current, displayLimit: newLimit });
      return newStates;
    });
  }

  // Get section state (returns default if not found)
  public getSectionState(sectionId: string): SectionState {
    const states = get(this.sectionStates);
    return states.get(sectionId) || { collapsed: false, displayLimit: 10 };
  }

  // Optional method for subclasses to provide section metadata
  protected generateSectionsWithMetadata?(items: T[]): SectionWithMetadata<T>[];

  constructor(items: Readable<T[]>, initialLimit: number = 50) {
    this.items = items;
    this.limit.set(initialLimit);

    // Set default datePreset filter to 'future' for calendar events
    this.filterDatePresetValues.set(['future']);

    // Initialize derived filtered items
    this.filteredItems = derived(
      [
        this.items,
        this.filterTagValues,
        this.filterSpaceValues,
        this.filterKeywordValue,
        this.filterCalendarValues,
        this.filterDatePresetValues,
        this.filterShowCompletedValue,
        this.filterMemoryValues,
        this.filterFavoriteValue,
        this.keywordIdSet,
        this.keywordIdOrder
      ],
      ([
        itemValues,
        tagValues,
        spaceValues,
        keywordValue,
        calendarValues,
        datePresetValues,
        showCompletedValue,
        memoryValues,
        favoriteValue,
        keywordIdSet,
        keywordIdOrder
      ]) => {
        // Pre-order items if we have an external ranking; otherwise use default sort
        const preOrderedItems = (() => {
          const isSearching = !!keywordValue.trim();

          if (isSearching && keywordIdOrder && keywordIdOrder.length > 0) {
            const idToRank = new Map<string, number>();
            keywordIdOrder.forEach((id, idx) => idToRank.set(id, idx));

            const itemsWithRank = itemValues.map((it, idx) => {
              const id = this.getItemId(it) ?? '';
              const rank = idToRank.has(id)
                ? (idToRank.get(id) as number)
                : Number.MAX_SAFE_INTEGER;
              return { it, rank, idx };
            });

            // Stable sort by rank first, then by original store order for same rank.
            // We explicitly avoid recency sort here to prevent shifting when items are modified
            // or when augmented favorites are appended.
            itemsWithRank.sort((a, b) => {
              if (a.rank !== b.rank) {
                return a.rank - b.rank;
              }
              return a.idx - b.idx;
            });

            return itemsWithRank.map((x) => x.it);
          }

          if (isSearching) {
            // When searching but no rank data yet (e.g. async gap or unsupported type),
            // avoid the recency sort fallback to prevent bubbling modified items to the top.
            return itemValues;
          }

          return this.sortItems(itemValues);
        })();

        return this.applyFilters(preOrderedItems, {
          tag: tagValues,
          space: spaceValues,
          keyword: keywordValue,
          keywordIdSet,
          calendarId: calendarValues,
          datePreset: datePresetValues,
          showCompleted: showCompletedValue,
          memory: memoryValues,
          favorite: favoriteValue
        });
      }
    );

    // Initialize canonical filters derived store (without counts)
    this.filters = derived(
      [workspaceStore, providerCalendars],
      ([workspaceData, providerCalendarsData]) => {
        return this.generateCanonicalFilters(workspaceData, providerCalendarsData);
      }
    );

    // Initialize mapped items store (unlimited, for virtual scrolling)
    this.mappedItems = derived(this.filteredItems, (filteredItems) => {
      if (!filteredItems) return [];
      return filteredItems.map((item) => {
        try {
          return this.mapItem(item);
        } catch (error) {
          console.error('[FilteredType] Error mapping item:', item, error);
          // Return item as-is if mapping fails (defensive programming)
          return item as unknown as U;
        }
      });
    });

    this.unlimitedSections = derived(this.filteredItems, (filteredItems) => {
      // Generate sections first, which filters out unneeded items.
      return this.generateSectionsFromRawItems(filteredItems).map((section) => ({
        title: section.title,
        items: section.items.map((item) => this.mapItem(item))
      }));
    });

    // Initialize derived stores after properties are set - use lazy mapping
    this.sections = derived([this.filteredItems, this.limit], ([filteredItems, limit]) => {
      // Generate sections from raw items first (using helper method)
      const allSections = this.generateSectionsFromRawItems(filteredItems);

      // Apply limit and lazy map only the items that will actually be displayed
      let remainingLimit = limit;
      const limitedSections: Section<U>[] = [];

      for (const section of allSections) {
        if (remainingLimit <= 0) break;

        const sectionItemsRaw = section.items.slice(0, remainingLimit);
        // Only map the items that will actually be displayed
        const sectionItemsMapped = sectionItemsRaw.map((item) => this.mapItem(item));

        limitedSections.push({
          title: section.title,
          items: sectionItemsMapped
        });

        remainingLimit -= sectionItemsRaw.length;
      }

      return limitedSections;
    });

    // Initialize sectionsWithState derived store
    this.sectionsWithState = derived(
      [this.filteredItems, this.sectionStates],
      ([filteredItems, sectionStatesMap]) => {
        // Get sections with metadata if available, otherwise use raw sections
        const sectionsWithMeta: SectionWithMetadata<T>[] = this.generateSectionsWithMetadata
          ? this.generateSectionsWithMetadata(filteredItems)
          : this.generateSectionsFromRawItems(filteredItems).map((section) => ({
              ...section,
              config: undefined
            }));

        // Apply state (displayLimit) to each section and map items
        return sectionsWithMeta.map((section) => {
          const sectionId = (section as SectionWithMetadata<T>).config?.id || section.title;
          const state = sectionStatesMap.get(sectionId) || { collapsed: false, displayLimit: 10 };

          // Apply displayLimit and map items
          const visibleItemsRaw = section.items.slice(0, state.displayLimit);
          const visibleItemsMapped = visibleItemsRaw.map((item) => this.mapItem(item));

          return {
            title: section.title,
            items: visibleItemsMapped,
            config: (section as SectionWithMetadata<T>).config
          } as SectionWithMetadata<U>;
        });
      }
    );

    // Initialize filters with counts - chained on items and canonical filters (not filteredItems)
    this.filtersWithCounts = derived([this.items, this.filters], ([items, canonicalFilters]) => {
      if (!this.enableFilterCounts) {
        // Return filters with selective counts for better performance during initial load
        return canonicalFilters.map((filter) => ({
          ...filter,
          counts: this.enabledCountTypes.has(filter.title)
            ? this.addCountsToSingleFilter(filter, items)
            : ({} as Record<string, number>)
        }));
      }
      return this.addCountsToFilters(canonicalFilters, items);
    });
  }

  // Getter for filtered items store - with lazy mapping
  public get itemsStore(): Readable<U[]> {
    return derived([this.filteredItems, this.limit], ([filteredItems, limit]) => {
      // Apply limit first, then map only what's needed
      const limitedItems = filteredItems.slice(0, limit);
      return limitedItems.map((item) => this.mapItem(item));
    });
  }

  public get itemsPlain(): U[] {
    const filteredItems = get(this.filteredItems);
    const currentLimit = get(this.limit);

    // Apply limit first to avoid mapping unnecessary items
    const limitedItems = filteredItems.slice(0, currentLimit);
    return limitedItems.map((item: T) => this.mapItem(item));
  }

  /**
   * Increase the current limit by the specified increment.
   *
   * @param increment - Number of items to add to the current limit
   */
  public increaseLimit(increment: number): void {
    console.log('increaseLimit', increment);
    this.limit.update((currentLimit) => currentLimit + increment);
  }

  /**
   * Get the current limit value.
   *
   * @returns Current limit value
   */
  public getCurrentLimit(): number {
    return get(this.limit);
  }

  /**
   * Get the total count of filtered items (before limiting).
   *
   * @returns Total count of filtered items
   */
  public getTotalFilteredCount(): number {
    return get(this.filteredItems).length;
  }

  /**
   * Enable filter counts calculation. Call this after initial load to improve performance.
   * Filter counts are disabled by default to speed up initial rendering.
   */
  public enableFilterCountsCalculation(): void {
    if (!this.enableFilterCounts) {
      this.enableFilterCounts = true;
      // Trigger recalculation by updating one of the derived store dependencies
      this.filters.subscribe(() => {}); // This will cause filtersWithCounts to recalculate
    }
  }

  /**
   * Check if filter counts are currently enabled.
   */
  public areFilterCountsEnabled(): boolean {
    return this.enableFilterCounts;
  }

  /**
   * Enable counts for specific filter types (e.g., 'Spaces', 'Tags').
   * This allows selective count enablement for performance optimization.
   */
  public enableCountsForFilterTypes(filterTypes: string[]): void {
    filterTypes.forEach((type) => this.enabledCountTypes.add(type));
    // Trigger recalculation by updating filters
    this.filters.subscribe(() => {}); // This will cause filtersWithCounts to recalculate
  }

  /**
   * Disable counts for specific filter types.
   */
  public disableCountsForFilterTypes(filterTypes: string[]): void {
    filterTypes.forEach((type) => this.enabledCountTypes.delete(type));
    // Trigger recalculation by updating filters
    this.filters.subscribe(() => {}); // This will cause filtersWithCounts to recalculate
  }

  /**
   * Get currently enabled count types.
   */
  public getEnabledCountTypes(): string[] {
    return Array.from(this.enabledCountTypes);
  }

  // Helper method to get a specific filter store by name
  public getFilterStore(filterName: string): Writable<any> | undefined {
    switch (filterName) {
      case 'tag':
        return this.filterTagValues;
      case 'space':
        return this.filterSpaceValues;
      case 'keyword':
        return this.filterKeywordValue;
      case 'calendar':
        return this.filterCalendarValues;
      case 'datePreset':
        return this.filterDatePresetValues;
      case 'showCompleted':
        return this.filterShowCompletedValue;
      case 'memory':
        return this.filterMemoryValues;
      case 'favorite':
        return this.filterFavoriteValue;
      default:
        return undefined;
    }
  }

  // Abstract method to be implemented by subclasses for section generation
  /**
   * Generate sections to group filtered and mapped items.
   *
   * This method receives items that have already been:
   * 1. Filtered based on current filter values
   * 2. Mapped from input type T to output type U
   *
   * @param items - Array of mapped items (type U) ready for sectioning
   * @returns Array of sections, each containing a title and array of items
   *
   *
   * Common sectioning strategies:
   * - Time-based: Today, This Week, This Month, etc.
   * - Priority-based: High, Medium, Low
   * - Status-based: Active, Completed, Archived
   * - Category-based: Work, Personal, Projects
   */
  protected abstract generateSections(items: U[]): Section<U>[];

  /**
   * Generate sections from raw items (before mapping) for lazy mapping optimization.
   * This allows us to section first, then only map the items that will be displayed.
   *
   * @param items - Array of raw items (type T) to section
   * @returns Array of sections containing raw items
   */
  public abstract generateSectionsFromRawItems(items: T[]): Section<T>[];

  // Abstract method to map input items to output items
  /**
   * Map input items to output items for UI consumption.
   *
   * This method transforms raw data items (type T) into UI-friendly items (type U).
   * The mapped items will be used for sectioning and display.
   *
   * @param item - Raw input item of type T
   * @returns Mapped item of type U ready for UI display
   *
   * @example
   * ```typescript
   * // FilteredMemory implementation - maps Transcript to MemoryItemType
   * protected mapItem(transcript: Transcript): MemoryItemType {
   *   const date = new Date(transcript.meeting?.date || transcript.createdAt || new Date());
   *   return {
   *     id: transcript.id,
   *     title: getTranscriptName(transcript) || 'Untitled',
   *     time: formatTime(date),
   *     fullDate: formatFullDate(date),
   *     summary: transcript.meeting?.minutes?.summary || '',
   *     category: transcript.meeting?.category || 'Main',
   *     categoryIcon: '􀧸'
   *   };
   * }
   * ```
   */
  protected abstract mapItem(item: T): U;

  /**
   * Generate canonical filters based on workspace data and provider calendars data.
   *
   * This method creates the base filter structure without counts.
   * It's derived from workspace store and provider calendars store to provide dynamic filters.
   *
   * @param workspaceData - Current workspace store data
   * @param providerCalendarsData - Current provider calendars data
   * @returns Array of filter definitions without counts
   */
  protected generateCanonicalFilters(
    workspaceData: any,
    providerCalendarsData: any
  ): Omit<Filter, 'counts'>[] {
    const filters: Omit<Filter, 'counts'>[] = [];

    // Add tag filter
    filters.push(createTagFilter());

    // Add space filter based on workspace data
    const spaceFilter = createSpaceFilter();
    if (workspaceData?.workspaces) {
      // Replace static options with dynamic workspace-based options
      spaceFilter.options = Array.from(workspaceData.workspaces.values() as any)
        .sort((a: any, b: any) => (Number(a.data?.weight) || 0) - (Number(b.data?.weight) || 0))
        .map((workspace: any) => ({
          value: workspace.id,
          label: workspace.name,
          iconProps: { name: workspace.data?.icon || ('folder-outline' as UnionIconValues) },
          kind: 'workspace' as const,
          workspace
        }));
    }
    filters.push(spaceFilter);

    // Add calendar filter with dynamic options based on provider calendars
    const calendarFilter = createCalendarFilter();
    if (providerCalendarsData && Object.keys(providerCalendarsData).length > 0) {
      // Replace static options with dynamic calendar-based options
      const calendarOptions = [];

      for (const [_providerId, providerCalendar] of Object.entries(providerCalendarsData)) {
        const calendar = providerCalendar as any;
        calendarOptions.push({
          value: calendar.id,
          label: calendar.data?.summary || calendar.data?.name || 'Calendar',
          iconProps: { name: 'calendar' as UnionIconValues },
          kind: 'calendar' as FilterOption['kind'],
          providerId: calendar.providerId,
          providerEmail: calendar.providerEmail,
          calendar
        });
      }

      if (calendarOptions.length > 0) {
        calendarFilter.options = calendarOptions;
      }
    }
    filters.push(calendarFilter);

    // Add date preset filter
    filters.push({
      title: 'Date Preset',
      options: [
        {
          value: 'future',
          label: 'Future',
          iconProps: { name: 'calendar' },
          kind: 'default'
        },
        {
          value: 'today',
          label: 'Today',
          iconProps: { name: 'calendar' },
          kind: 'default'
        },
        {
          value: 'this-week',
          label: 'This Week',
          iconProps: { name: 'calendar' },
          kind: 'default'
        },
        {
          value: 'this-month',
          label: 'This Month',
          iconProps: { name: 'calendar' },
          kind: 'default'
        },
        {
          value: 'past-90-days',
          label: 'Past 90 Days',
          iconProps: { name: 'calendar' },
          kind: 'default'
        }
      ],
      icon: 'calendar'
    });

    // Add show completed filter
    filters.push({
      title: 'Show Completed',
      options: [
        {
          value: 'true',
          label: 'Show Completed',
          iconProps: { name: 'check-circle-outline' },
          kind: 'default'
        },
        {
          value: 'false',
          label: 'Hide Completed',
          iconProps: { name: 'user-circle-outline' },
          kind: 'default'
        }
      ],
      icon: 'check-circle-outline'
    });

    // Add memory filter
    filters.push({
      title: 'Memory',
      options: [
        {
          value: 'all',
          label: 'All Memories',
          iconProps: { name: 'pin-outline' },
          kind: 'default'
        },
        { value: 'recent', label: 'Recent', iconProps: { name: 'home-outline' }, kind: 'default' },
        {
          value: 'important',
          label: 'Important',
          iconProps: { name: 'list-outline' },
          kind: 'default'
        }
      ],
      icon: 'pin-outline'
    });

    return filters;
  }

  /**
   * Add counts to canonical filters based on all items.
   *
   * This method calculates counts for each filter option based on the complete item set,
   * not the filtered items, to avoid circular dependencies.
   *
   * @param filters - Canonical filters without counts
   * @param items - All items to count against
   * @returns Filters with counts added
   */
  protected addCountsToFilters(filters: Omit<Filter, 'counts'>[], items: T[]): Filter[] {
    return filters.map((filter) => {
      const counts: Record<string, number> = {};

      // Calculate counts for each option in this filter
      for (const option of filter.options) {
        counts[option.value] = this.calculateCountForFilterOption(
          filter.title,
          option.value,
          items
        );
      }

      return {
        ...filter,
        counts
      };
    });
  }

  /**
   * Add counts to a single filter (for selective count enablement).
   */
  protected addCountsToSingleFilter(
    filter: Omit<Filter, 'counts'>,
    items: T[]
  ): Record<string, number> {
    const counts: Record<string, number> = {};

    // Calculate counts for each option in this filter
    for (const option of filter.options) {
      counts[option.value] = this.calculateCountForFilterOption(filter.title, option.value, items);
    }

    return counts;
  }

  /**
   * Calculate count for a specific filter option based on all items.
   *
   * This method works directly with raw items to avoid expensive mapping operations
   * during filter count calculations.
   *
   * @param filterTitle - The title of the filter (e.g., 'Tags', 'Spaces')
   * @param optionValue - The value of the specific option to count
   * @param items - All items to count against
   * @returns The count of items matching this filter option
   */
  protected calculateCountForFilterOption(
    filterTitle: string,
    optionValue: string,
    items: T[]
  ): number {
    // Work directly with raw items - no mapping needed for counts!
    switch (filterTitle) {
      case 'Tags':
        return this.calculateTagCount(optionValue, items);
      case 'Spaces':
        return this.calculateSpaceCount(optionValue, items);
      case 'Calendars':
        return this.calculateCalendarCount(optionValue, items);
      case 'Date Preset':
        return this.calculateDatePresetCount(optionValue, items);
      case 'Show Completed':
        return this.calculateShowCompletedCount(optionValue, items);
      case 'Memory':
        return this.calculateMemoryCount(optionValue, items);
      default:
        return 0;
    }
  }

  // Base class count calculation methods - subclasses can override these
  protected calculateTagCount(tagValue: string, items: T[]): number {
    return items.filter((item) => {
      const itemTags = this.getItemTags(item);
      return itemTags.includes(tagValue);
    }).length;
  }

  protected calculateSpaceCount(spaceValue: string, items: T[]): number {
    return items.filter((item) => {
      const itemSpace = this.getItemSpace(item);
      return itemSpace === spaceValue;
    }).length;
  }

  protected calculateCalendarCount(calendarValue: string, items: T[]): number {
    return items.filter((item) => {
      const itemCalendar = this.getItemCalendar(item);
      return itemCalendar === calendarValue;
    }).length;
  }

  protected calculateDatePresetCount(_datePresetValue: string, items: T[]): number {
    // This is a simplified implementation - subclasses can override for more sophisticated date logic
    return items.length; // For now, return total count for all date presets
  }

  protected calculateShowCompletedCount(showCompletedValue: string, items: T[]): number {
    const showCompleted = showCompletedValue === 'true';
    const completedItems = items.filter((item) => this.isItemCompleted(item));
    return showCompleted ? completedItems.length : items.length - completedItems.length;
  }

  protected calculateMemoryCount(_memoryValue: string, items: T[]): number {
    // This is a simplified implementation - subclasses can override for more sophisticated memory logic
    return items.length; // For now, return total count for all memory options
  }

  // Helper methods for extracting filterable properties from items
  // These can be overridden by subclasses for custom extraction logic

  /**
   * Extract tags from an item for filtering.
   *
   * @param item - The item to extract tags from
   * @returns Array of tag strings
   */
  protected abstract getItemTags(_item: T): string[];

  /**
   * Extract space (workspace) from an item for filtering.
   *
   * @param item - The item to extract space from
   * @returns Space identifier string or undefined
   */
  protected abstract getItemSpace(item: T): string | undefined;

  /**
   * Extract searchable text from an item for keyword filtering.
   *
   * @param item - The item to extract text from
   * @returns Searchable text string
   */
  protected abstract getItemText(item: T): string;

  /**
   * Extract calendar identifier from an item for filtering.
   *
   * @param item - The item to extract calendar from
   * @returns Calendar identifier string or undefined
   */
  protected abstract getItemCalendar(item: T): string | undefined;

  /**
   * Extract memory identifier from an item for filtering.
   *
   * @param item - The item to extract memory from
   * @returns Memory identifier string or undefined
   */
  protected abstract getItemMemory(item: T): string | undefined;

  /**
   * Check if an item is completed for completion filtering.
   *
   * @param item - The item to check
   * @returns True if the item is completed
   */
  protected isItemCompleted(item: T): boolean {
    return (item as any)?.completedAt !== undefined;
  }

  protected isItemDeleted(item: T): boolean {
    return (item as any)?.deletedAt !== undefined;
  }

  /**
   * Check if an item matches the specified date preset filter.
   *
   * @param item - The item to check
   * @param datePresetValues - Array of date preset values to match against
   * @returns True if the item matches any of the date preset values
   */
  protected matchesDatePreset(_item: T, _datePresetValues: string[]): boolean {
    // Default implementation - override in subclasses for date-based filtering
    return true;
  }

  /**
   * Apply filters to items based on current filter values.
   *
   * This method implements the core filtering logic, checking each item
   * against all active filters and returning only matching items.
   *
   * @param items - All available items to filter
   * @param filterValues - Current filter values to apply
   * @returns Array of items that match all active filters
   */
  protected applyFilters(items: T[], filterValues: any): T[] {
    // Pre-compile filters to avoid repeated creation
    const hasTagFilter = filterValues.tag.length > 0;
    const tagFilter = hasTagFilter ? createMultiSelectFilter(filterValues.tag) : null;

    const hasSpaceFilter = filterValues.space.length > 0;
    const spaceFilter = hasSpaceFilter ? createMultiSelectFilter(filterValues.space) : null;

    const hasKeywordFilter = filterValues.keyword.trim();
    const keywordLower = hasKeywordFilter ? filterValues.keyword.toLowerCase() : '';
    const externalIdSet: Set<string> | null = filterValues.keywordIdSet || null;

    const hasCalendarFilter = filterValues.calendarId.length > 0;
    const calendarFilter = hasCalendarFilter
      ? createMultiSelectFilter(filterValues.calendarId)
      : null;

    const hasDatePresetFilter = filterValues.datePreset.length > 0;

    const hasMemoryFilter = filterValues.memory.length > 0;
    const memoryFilter = hasMemoryFilter ? createMultiSelectFilter(filterValues.memory) : null;

    return items.filter((item) => {
      // Deleted filter (early exit)
      if (this.isItemDeleted(item)) {
        return false;
      }

      // Tag filter
      if (hasTagFilter && tagFilter) {
        const itemTags = this.getItemTags(item);
        if (!itemTags.some((tag) => tagFilter(tag))) {
          return false;
        }
      }

      // Space filter
      if (hasSpaceFilter && spaceFilter) {
        const itemSpace = this.getItemSpace(item);
        if (!spaceFilter(itemSpace)) {
          return false;
        }
      }

      // Keyword filter
      if (hasKeywordFilter) {
        const itemId = this.getItemId(item);
        const isFav =
          (item as any).isFavorite === true ||
          (item as any).isFavorite === 1 ||
          (item as any).isFavorite === 'true';

        if (externalIdSet) {
          // If we have external search results (like Tantivy), use those
          const matchesExternal = itemId && externalIdSet.has(itemId);

          if (!matchesExternal) {
            // ONLY augment search results with favorites if the favorite filter is actually ON
            // This prevents search results from shifting or including extra items when the user
            // hasn't specifically asked for favorites.
            if (filterValues.favorite && isFav) {
              const itemText = this.getItemText(item);
              if (!itemText.toLowerCase().includes(keywordLower)) {
                return false;
              }
            } else {
              return false;
            }
          }
        } else {
          // Fallback to local search
          const itemText = this.getItemText(item);
          if (!itemText.toLowerCase().includes(keywordLower)) {
            return false;
          }
        }
      }

      // Calendar filter
      if (hasCalendarFilter && calendarFilter) {
        const itemCalendar = this.getItemCalendar(item);
        if (!calendarFilter(itemCalendar)) {
          return false;
        }
      }

      // Date preset filter
      if (hasDatePresetFilter) {
        if (!this.matchesDatePreset(item, filterValues.datePreset)) {
          return false;
        }
      }

      // Memory filter
      if (hasMemoryFilter && memoryFilter) {
        const itemMemory = this.getItemMemory(item);
        if (!memoryFilter(itemMemory)) {
          return false;
        }
      }

      // Favorite filter
      if (filterValues.favorite) {
        const isFav =
          (item as any).isFavorite === true ||
          (item as any).isFavorite === 1 ||
          (item as any).isFavorite === 'true';
        if (!isFav) {
          return false;
        }
      }

      // Show completed filter
      if (!filterValues.showCompleted && this.isItemCompleted(item)) {
        return false;
      }

      return true;
    });
  }

  /**
   * Extract unique identifier from an item. Defaults to reading `id` field.
   * Subclasses can override for custom ID logic.
   */
  protected getItemId(item: T): string | undefined {
    return (item as any)?.id as string | undefined;
  }

  // Allow subclasses/consumers to provide external keyword match IDs
  public setKeywordExternalIdSet(idSet: Set<string> | null): void {
    this.keywordIdSet.set(idSet);
  }

  public setKeywordExternalIdOrder(order: string[] | null): void {
    this.keywordIdOrder.set(order);
  }
}
